import CardSlot from '@gp/components/CardSlot';
import type {
  IDateValue,
  IGranularity,
} from '@gp/components/ComposedDatePicker/types';
import TimeChangeCheckbox from '@gp/components/TimeChangeCheckbox';
import usePageFilterInit from '@gp/hooks/usePageFilterInit';
import usePageStore from '@gp/hooks/usePageStore';
import useRequest from '@gp/hooks/useRequest';
import {
  CALENDAR_CARD_SIZES,
  CALENDAR_CARD_WIDTH_MAPPING,
  DEFAULT_CALENDAR_CARD_SIZE,
  MAX_CALENDAR_CARD_SIZE,
  MIN_CALENDAR_CARD_SIZE,
} from '@gp/pages/Pipeline/Overview/components/CalendarPanel/constants';
import { fetchCommonConditions } from '@gp/services/filter';
import type { IShortUrlParams } from '@gp/types/global';
import { reportBtnOperationLog } from '@gp/utils/common';
import { deleteFilterAllValue } from '@gp/utils/utils';
import FullScreenScale from '@home/components/FullScreenScale';
import SvgIcon from '@home/components/SvgIcon';
import type { IBaseProps, INullableMoment } from '@home/types/common';
import { useMemoizedFn, useSize, useUnmount } from 'ahooks';
import classnames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble, Icon, SelectOptionWithGroup } from 'tea-component';
import type { ICalendarPanelInstance } from '../CalendarPanel';
import CalendarPanel from '../CalendarPanel';
import CalendarTable from './components/CalendarTable';
import DownloadModal from './components/DownloadModal';
import Filter from './components/Filter';
import TopBarLeftElementCom from './components/TopBarLeftElementCom';
import './fullscreen.scss';
import useCalendar from './hooks/useCalendar';
import useCalendarMonthly from './hooks/useCalendarMonthly';
import useInit from './hooks/useInit';
import useOptionsAndValues from './hooks/useOptionsAndValues';
import useScalable from './hooks/useScalable';
import './index.scss';
import type { ILockerData } from './models/locker';
import { DEFAULT_FILTER, LOCKER_MODEL_NAMESPACE } from './models/locker';
import {
  formatDateForFilter,
  getCalendarDataFetchParams,
  getDefaultDates,
  getMilestoneOptions,
  getMilestoneOptionsForms,
} from './utils';
import { FIELDS_SCHEDULE, TAB_LOG_KEYS } from './utils/constants';
type IScheduleProps = IBaseProps;

const formatRangeDateStr = (
  date: string,
  granularity: IGranularity,
  action: 'startOf' | 'endOf',
): string => {
  const dateM: INullableMoment = date ? moment(date) : null;
  return !dateM?.isValid()
    ? ''
    : dateM[action]?.(granularity).format('YYYY-MM-DD');
};

const getRangeDate = (
  granularity: IGranularity,
  startDate: string,
  endDate: string,
): IDateValue =>
  [startDate, endDate].map((date, dateIdx) => {
    const action = dateIdx === 0 ? 'startOf' : 'endOf';
    return formatRangeDateStr(date, granularity, action);
  }) as IDateValue;

const Schedule: React.FC<IScheduleProps> = (props) => {
  const {
    style,
    className,
    scrollBody,
    headerTitle,
    headerOther,
    currentPage,
    hasSearchBtn,
    searchPlaceholder,
    formatSearchResults,
    getSearchList,
    searchList,
    setSearchList,
    searchVal,
    setSearchVal,
  } = props;

  const { width } = useSize(document.body) || {};
  const filterHeightRef = useRef(null);
  const [isSingLine, setIsSingLine] = useState(false);
  const [isOverFiler, setIsOverFiler] = useState(true);
  const [selectedValue, setSelectedValue] = useState<any>({});
  const { lockerData, setLockerData } = usePageStore<ILockerData>(
    LOCKER_MODEL_NAMESPACE,
  );
  const [milestoneDateType, setMilestoneDateType] = useState<any>('quarter');

  // 取消segment切换功能，全部默认为'2';
  const activeSegment = '2';
  const {
    lock,
    tab: lockerTab,
    segment: lockerSegment,
    filter: lockerFilter,
    timeChange: lockerTimeChange,
  } = lockerData || {};

  const commonCondParams = {
    milestone_time_range: [],
    pteam_bo_studio_project: {
      project_auth: false,
      sensitive_filter_auth: true,
    },
    platform: [],
    official_gate_review: [],
    milestone: [],
    launch_milestone: [],
    publisher: [],
    dps_status: [],
    stage: [],
    group: [],
  };

  const { loading: filterLoading, data: filterList } = useRequest(
    fetchCommonConditions,
    {
      defaultParams: [commonCondParams],
    },
  );

  const {
    options,
    values,
    defaultValues: allValuesInterface,
  } = useOptionsAndValues(filterLoading, filterList);

  // 根据接口返回的三个tab的milestone替换掉之前前端写死的
  const milestoneDataList = useMemo(
    () => ({
      1: getMilestoneOptionsForms(filterList?.official_gate_review || []),
      2: getMilestoneOptionsForms(filterList?.milestone || []),
      3: getMilestoneOptionsForms(filterList?.launch_milestone || []),
    }),
    [filterList],
  );

  // 日期的选择范围
  const dateRange = useMemo<
  { month: IDateValue; quarter: IDateValue; year: IDateValue; } | undefined
  >(() => {
    if (filterLoading) return void 0;
    // const { start_date: startDate, end_date: endDate } = filterList || {};
    const { milestone_time_range = {} } = filterList || {};
    const { start_date: startDate, end_date: endDate } = milestone_time_range;
    if (!startDate || !endDate) return void 0;

    return {
      month: getRangeDate('month', startDate, endDate),
      quarter: getRangeDate('quarter', startDate, endDate),
      year: getRangeDate('year', startDate, endDate),
    };
  }, [filterLoading, filterList]);

  // 日期的默认值（月、季、年）
  const defaultDate = useMemo<
  { month: IDateValue; quarter: IDateValue; year: IDateValue; } | undefined
  >(() => {
    if (filterLoading) return void 0;
    return getDefaultDates(dateRange);
  }, [filterLoading, dateRange]);

  const {
    loading: initialLoading,
    tab: initialTab,
    segment: initialSegment,
    filter: initialFilter,
    onUpdateShortUrlParams,
  } = usePageFilterInit({
    loading: filterLoading,
    conditions: {
      tab: ['calendar-card', 'calendar-table'],
      segment: ['1', '2', '3'],
      filter: {
        ...options,
        milestone: (params?: IShortUrlParams) => {
          const { segment } = params || {};
          return getMilestoneOptions(
            (segment || '2') as any,
            milestoneDataList,
          );
        },
        composedDatePicker: (params?: IShortUrlParams) => {
          const { tab } = params || {};
          return {
            showGranularitySelect: tab === 'calendar-card',
            range: dateRange,
            defaultDate,
          };
        },
      },
    },
    storeValues: {
      lock,
      tab: lockerTab,
      segment: lockerSegment,
      filter: lockerFilter,
    },
    defaultValues: {
      tab: 'calendar-card',
      segment: '',
      filter: {
        ...DEFAULT_FILTER,
        stage: values?.stage || [],
        project_id: values?.project_id || [],
      },
    },
    allFilterOptionsValues: allValuesInterface,
    setSelectedValue,
    fields: FIELDS_SCHEDULE,
  });

  const defaultValuesCur = useMemo(() => {
    return {
      ...DEFAULT_FILTER,
      stage: values?.stage || [],
      project_id: values?.project_id || [],
    };
  }, [DEFAULT_FILTER, values]);

  // 日历、月历
  const [activeTab, setActiveTab] = useState<ScheduleTabTypes>(
    initialTab as any,
  );
  const [category, setCategory] = useState([]);
  const defaultComposedDatePickerValue = useMemo(() => {
    const defaultGranularity =
      activeTab === 'calendar-card' ? 'quarter' : 'month';
    return {
      date: defaultDate?.[defaultGranularity] || ['', ''],
      granularity: defaultGranularity,
    };
  }, [activeTab, defaultDate]);

  // 筛选器
  const [filter, setFilter] = useState<IPipelineOverviewScheduleFilterVO>(
    initialFilter as any,
  );

  // time change开关
  const [timeChange, setTimeChange] = useState<boolean>(
    lock ? lockerTimeChange : false,
  );
  const milestoneOptions = useMemo<IGPSelectOptionWithGroup[]>(
    () => getMilestoneOptions(activeSegment, milestoneDataList),
    [activeSegment, milestoneDataList],
  );

  const fetchParams = useMemo(
    () =>
      getCalendarDataFetchParams(
        activeSegment,
        filter,
        options,
        milestoneOptions,
      ),
    [activeSegment, filter, options, milestoneOptions],
  );

  /** ************* Filter筛选器 ************* **/
  // 是否展示时间粒度切换下拉框
  const [showGranularitySelect, setShowGranularitySelect] = useState(
    activeTab === 'calendar-card',
  );

  const updateCalendarData = useMemoizedFn(
    (
      tab: ScheduleTabTypes,
      segment: ScheduleSegmentTypes,
      filter: IPipelineOverviewScheduleFilterVO,
      milestoneOptionsPayload: SelectOptionWithGroup[] = milestoneOptions,
    ) => {
      const params = getCalendarDataFetchParams(
        segment,
        filter,
        options,
        milestoneOptionsPayload,
      );
      if (tab === 'calendar-card') {
        getScheduleDataCancel();
        getScheduleDataRun(params);
      } else if (tab === 'calendar-table') {
        getScheduleMonthlyDataCancel();
        getScheduleMonthlyDataRun(params);
      }
    },
  );

  const handleFilterChange = useMemoizedFn(
    (payload: IPipelineOverviewScheduleFilterVO) => {
      const {
        composedDatePicker,
        business_owner: businessOwner,
        studio,
        project,
        platform,
        form,
        status,
        stage,
        publisher,
        pteam,
        project_id,
      } = payload || {};

      const { granularity, date } = composedDatePicker || {};
      const [startDate, endDate] = date || [];
      // 如果是calendar-card,保存变化的粒度
      if (activeTab === 'calendar-card') {
        setMilestoneDateType(granularity);
      }

      const startDateTmp = formatDateForFilter(
        granularity,
        startDate,
        'startOf',
      );
      const endDateTmp = formatDateForFilter(granularity, endDate, 'endOf');
      const filterTmp = {
        ...filter,
        composedDatePicker: {
          ...composedDatePicker,
          date: [startDateTmp, endDateTmp] as IDateValue,
        },
        pteam: pteam || [],
        business_owner: businessOwner || [],
        studio: studio || [],
        project: project || [],
        platform: platform || [],
        milestone: [],
        // milestone: milestone || [],
        publisher: publisher || [],
        status: [],
        // status: status || [],
        stage: stage || [],
        form: form || [],
        project_id: project_id || [],
      };
      setFilter(filterTmp);
      updateCalendarData(activeTab, activeSegment, filterTmp);
      onUpdateShortUrlParams({
        tab: activeTab,
        segment: activeSegment,
        filter: filterTmp,
      });
    },
  );
  /** ************* Filter筛选器 ************* **/

  const reSetFun = () => {
    const defaultValues = {
      ...defaultValuesCur,
      milestone: [],
      // milestone: defaultMilestoneValue,
      composedDatePicker: defaultComposedDatePickerValue,
    } as any;
    setTimeChange(false);
    setFilter(defaultValues);
    updateCalendarData(activeTab, activeSegment, defaultValues);
    setSelectedValue({});
    deleteFilterAllValue();
    setCategory([]);
  };

  /**
   * tab更新时的逻辑
   * 1、如果是月历，则把当前时间转换为月粒度的时间（开始时间为当前时间粒度对应的开始时间，结束时间为当前时间粒度对应的结束时间）
   * 2、更新日期筛选器的时间粒度下拉组件的选中项为月粒度 并且禁用
   */
  const handleActiveTabChange = (payload: ScheduleTabTypes) => {
    const isCalendarTable = payload === 'calendar-table';
    const { composedDatePicker } = filter || {};
    const { granularity, date } = composedDatePicker || {};
    const [startDate, endDate] = date || [];
    const startDateM: moment.Moment | null = !startDate
      ? null
      : moment(startDate);
    const endDateM: moment.Moment | null = !endDate ? null : moment(endDate);

    if (isCalendarTable) {
      const startDateTmp = !startDateM?.isValid()
        ? ''
        : startDateM?.startOf(granularity).format('YYYY-MM-DD');
      const endDateTmp = !endDateM?.isValid()
        ? ''
        : endDateM?.endOf(granularity).format('YYYY-MM-DD');
      const dateTmp: IDateValue =
        !startDateTmp || !endDateTmp ? ['', ''] : [startDateTmp, endDateTmp];
      const filterTmp = {
        ...filter,
        composedDatePicker: {
          ...composedDatePicker,
          granularity: 'month' as ScheduledateTypes,
          date: dateTmp,
        },
      };
      setFilter(filterTmp);
      updateCalendarData(payload, activeSegment, filterTmp);
      onUpdateShortUrlParams({
        tab: payload,
        segment: activeSegment,
        filter: filterTmp,
      });
    } else if (payload === 'calendar-card') {
      const startDateTmp = formatDateForFilter(
        granularity,
        startDate,
        'startOf',
      );
      const endDateTmp = formatDateForFilter(granularity, endDate, 'endOf');

      const filterTmp = {
        ...filter,
        composedDatePicker: {
          ...composedDatePicker,
          granularity: milestoneDateType,
          date: [startDateTmp, endDateTmp] as IDateValue,
        },
      } as any;
      setFilter(filterTmp);
      updateCalendarData(payload, activeSegment, filterTmp);
      onUpdateShortUrlParams({
        tab: payload,
        segment: activeSegment,
        filter: filterTmp,
      });
    }

    setShowGranularitySelect(!isCalendarTable);
    setActiveTab(payload);
  };

  const handleCategoryChange = (payload: any) => {
    setCategory(payload);
  };

  /** ************* 日历全屏、缩放 ************* **/
  const calendarPanel = useRef<ICalendarPanelInstance | null>(null);
  const {
    size: calendarCardSize,
    onSmallerSize: handleCalendarCardSmallerSize,
    onBiggerSize: handleCalendarCardBiggerSize,
    reset: resetCalendarCardSize,
  } = useScalable({
    initialSize: DEFAULT_CALENDAR_CARD_SIZE,
    sizes: CALENDAR_CARD_SIZES,
    onSizeChange: (size) => {
      calendarPanel.current?.onCardSizeChange(size);
    },
  });
  /** ************* 日历全屏、缩放 ************* **/

  /** ************* 下载 ************* **/
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  /** ************* 下载 ************* **/

  /** ************* 日历 ************* **/
  const {
    calendarDataLoading,
    calendarDataFiltered,
    getScheduleDataRun,
    getScheduleDataRunAsync,
    getScheduleDataCancel,
  } = useCalendar({
    timeChange,
    filter,
    category,
  });
  /** ************* 日历 ************* **/

  /** ************* 月历 ************* **/
  const {
    calendarMonthlyDataLoading,
    calendarMonthlyDataNoData,
    calendarMonthlyListFiltered,
    getScheduleMonthlyDataRun,
    getScheduleMonthlyDataRunAsync,
    getScheduleMonthlyDataCancel,
  } = useCalendarMonthly(timeChange, category);
  /** ************* 月历 ************* **/

  /**
   * 页面初始化逻辑
   */
  useInit({
    tab: initialTab as any,
    setTab: setActiveTab,
    segment: initialSegment as any,
    setShowGranularitySelect,
    defaultDate,
    options,
    values,
    filterLoading: initialLoading,
    filter: initialFilter as any,
    setFilter,
    updateCalendarData,
    getScheduleDataRunAsync,
    getScheduleMonthlyDataRunAsync,
    onUpdateShortUrlParams,
  });

  /** ************* 打点 ************* **/
  useEffect(() => {
    reportBtnOperationLog(TAB_LOG_KEYS[activeTab]);
  }, [activeTab]);
  /** ************* 打点 ************* **/

  useUnmount(() => {
    setLockerData({
      tab: activeTab,
      segment: activeSegment,
      filter,
      timeChange,
    });
  });

  useEffect(() => {
    const handleResize = () => {
      if (filterHeightRef.current) {
        const filterRefHeight =
          filterHeightRef.current?.getBoundingClientRect().height;
        if (filterRefHeight > 102) {
          setIsSingLine(false);
        } else {
          setIsSingLine(true);
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [filterLoading, calendarDataLoading, calendarMonthlyDataLoading]);

  const calendLaodingCur = useMemo(() => {
    return activeTab === 'calendar-card'
      ? calendarDataLoading
      : calendarMonthlyDataLoading;
  }, [activeTab, calendarDataLoading, calendarMonthlyDataLoading]);

  const clickTimeChange = (payload: any) => {
    // 添加按钮打点
    const btnParams = {
      extInfo: 'Overview-Schedule-Time Change',
    };
    reportBtnOperationLog('PIPELINE_DASHBOARD_SCHEDULE_TIME_CHANGE', btnParams);
    setTimeChange(payload);
  };

  const topBarRightElementCur = useMemo(() => {
    return (
      <div className="dbg-project-overview-schedule-top-right">
        <TimeChangeCheckbox
          disabled={filterLoading || calendLaodingCur}
          value={timeChange}
          onChange={(payload) => {
            clickTimeChange(payload);
          }}
        />
        <div className={'dbg-card-slot-flex'}>
          <Bubble dark>
            {isDownloading ? (
              <i className="dbg-card-slot-char">
                <Icon type="loading" />
              </i>
            ) : (
              <i
                className="dbg-card-slot-char"
                onClick={() => {
                  setIsDownloadModalVisible(true);
                }}
              >
                <SvgIcon className="dbg-card-slot-charicon" type="download" />
              </i>
            )}
          </Bubble>
        </div>
      </div>
    );
  }, [isDownloading, calendLaodingCur, timeChange, filterLoading]);

  const topBarLeftElementFun = () => {
    return (
      <TopBarLeftElementCom
        activeTab={activeTab}
        category={category}
        filterLoading={filterLoading}
        calendLaodingCur={calendLaodingCur}
        handleActiveTabChange={handleActiveTabChange}
        handleCategoryChange={handleCategoryChange}
      />
    );
  };

  return (
    <div
      className={classnames('dbg-project-overview-schedule', {
        'is-need-top': !isOverFiler,
      })}
    >
      <div className="dbg-project-overview-schedule-head-title">
        <h3 className="dbg-pipeline-justify-container-title">{'Schedule'}</h3>
      </div>

      <Filter
        loading={filterLoading}
        data={{
          ...options,
          milestone: milestoneOptions,
          composedDatePicker: {
            showGranularitySelect,
            range: dateRange,
            defaultDate,
          },
        }}
        value={filter}
        onChange={handleFilterChange}
        onReset={reSetFun}
        allFilterOptionsValues={allValuesInterface}
        setIsOverFiler={setIsOverFiler}
        scrollBody={scrollBody}
        style={{ marginBottom: 0 }}
        headerTitle={headerTitle}
        headerOther={headerOther}
        currentPage={currentPage}
        hasSearchBtn={hasSearchBtn}
        searchPlaceholder={searchPlaceholder}
        formatSearchResults={formatSearchResults}
        getSearchList={getSearchList}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        searchList={searchList}
        setSearchList={setSearchList}
        searchVal={searchVal}
        setSearchVal={setSearchVal}
      />
      <CardSlot
        style={style}
        className={classnames('dbg-dashboard-schedule-calendar', className, {
          'dbg-dashboard-schedule-calendar-small':
            width !== undefined && width <= 1460,
        })}
        activeTab={activeTab}
        isDownload={true}
        downloadLoading={isDownloading}
        onDownload={() => {
          setIsDownloadModalVisible(true);
        }}
        getChange={handleActiveTabChange}
      >
        <FullScreenScale
          title="Schedule"
          smallerBtnDisabled={calendarCardSize === MIN_CALENDAR_CARD_SIZE}
          biggerBtnDisabled={calendarCardSize === MAX_CALENDAR_CARD_SIZE}
          onSmaller={handleCalendarCardSmallerSize}
          onBigger={handleCalendarCardBiggerSize}
          onFullScreenChange={() => {
            resetCalendarCardSize();
          }}
        >
          {({ isFullScreen, fullScreenContent }) => (
            <>
              {/* 避免全屏气泡不出来。所以挂载 才传fullScreenContent */}
              {activeTab === 'calendar-card' ? (
                <CalendarPanel
                  ref={calendarPanel}
                  isDashboardCalendar
                  isLoading={filterLoading || calendarDataLoading}
                  calendarCardSize={calendarCardSize}
                  fullScreenContent={fullScreenContent}
                  fixedBottomScrollbarStyle={{ bottom: 10 }}
                  dateType={filter?.composedDatePicker?.granularity}
                  data={calendarDataFiltered || []}
                  topBarLeftElement={topBarLeftElementFun()}
                  topBarRightElement={topBarRightElementCur}
                  reSetFun={reSetFun}
                  isSingLine={isSingLine}
                  category={category}
                />
              ) : (
                <CalendarTable
                  tableWidth={CALENDAR_CARD_WIDTH_MAPPING[calendarCardSize]}
                  isLoading={filterLoading || calendarMonthlyDataLoading}
                  isNoData={calendarMonthlyDataNoData}
                  isFullScreen={isFullScreen}
                  fullScreenContent={fullScreenContent}
                  dateType={filter?.composedDatePicker?.granularity}
                  data={calendarMonthlyListFiltered}
                  topBarLeftElement={topBarLeftElementFun()}
                  topBarRightElement={topBarRightElementCur}
                  reSetFun={reSetFun}
                  isSingLine={isSingLine}
                />
              )}
            </>
          )}
        </FullScreenScale>
      </CardSlot>
      {/* 下载弹窗 */}
      <DownloadModal
        tab={activeTab}
        segment={activeSegment}
        fetchParams={fetchParams}
        visible={isDownloadModalVisible}
        setVisible={setIsDownloadModalVisible}
        setIsDownloading={setIsDownloading}
      />
    </div>
  );
};

export default React.memo(Schedule);
